import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Typography,
  Paper,
  Grid as MuiGrid,
  TextField,
  Button,
  Box,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Person } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { RootState, AppDispatch } from '../../store';
import * as api from '../../services/api';
import { updateUser } from '../../store/slices/authSlice';
import { useI18n } from '../../i18n/useI18n';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';

const Grid = MuiGrid as any;

const PRO_WARM = '#ff8ba7';
const PRO_PINK_DEEP = '#e86a8a';

const outlinedPinkBtn = {
  borderRadius: 2,
  textTransform: 'none' as const,
  fontWeight: 600,
  color: PRO_PINK_DEEP,
  borderColor: alpha(PRO_PINK_DEEP, 0.45),
  bgcolor: alpha(PRO_WARM, 0.12),
  '&:hover': {
    borderColor: PRO_PINK_DEEP,
    bgcolor: alpha(PRO_WARM, 0.22),
  },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    boxShadow: `0 1px 3px ${alpha(PRO_PINK_DEEP, 0.06)}`,
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': {
      borderColor: alpha(PRO_WARM, 0.42),
    },
    '&:hover fieldset': {
      borderColor: PRO_PINK_DEEP,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(PRO_PINK_DEEP, 0.16)}`,
      '& fieldset': {
        borderWidth: 1.5,
        borderColor: PRO_PINK_DEEP,
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 1.35,
    fontSize: '0.95rem',
    fontWeight: 600,
  },
};

const Profile: React.FC = () => {
  const { pages: t } = useI18n();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading: authLoading } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setIsInitialLoad(false);
    } else if (!authLoading) {
      setIsInitialLoad(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (user && (formData.username !== user.username || formData.email !== user.email)) {
      setFormData((prev) => ({
        ...prev,
        username: user.username || '',
        email: user.email || '',
      }));
    }
  }, [user?.username, user?.email]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoading(true);

      if (formData.newPassword || formData.currentPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('Mật khẩu mới không khớp');
          setIsLoading(false);
          return;
        }
        if (!formData.currentPassword) {
          toast.error('Vui lòng nhập mật khẩu hiện tại');
          setIsLoading(false);
          return;
        }
      }

      try {
        const updatedUser = await api.updateProfile({
          username: formData.username,
          email: formData.email,
          currentPassword: formData.currentPassword || undefined,
          newPassword: formData.newPassword || undefined,
        });

        dispatch(updateUser(updatedUser.user));
        toast.success('Cập nhật thông tin thành công');

        setFormData((prev) => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      } catch (error: any) {
        console.error('Update error:', error);
        const errorMessage = error.response?.data?.error || 'Cập nhật thông tin thất bại';
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [formData, dispatch],
  );

  const handleAvatarFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        setUploadingAvatar(true);
        const fd = new FormData();
        fd.append('avatar', file);
        const updated = await api.uploadProfileAvatar(fd);
        dispatch(updateUser(updated.user));
        toast.success('Đã cập nhật ảnh đại diện');
      } catch (error: any) {
        const errorMessage = error?.response?.data?.error || 'Cập nhật ảnh đại diện thất bại';
        toast.error(errorMessage);
      } finally {
        setUploadingAvatar(false);
        if (e.target) e.target.value = '';
      }
    },
    [dispatch],
  );

  if (isInitialLoad && !user) {
    return (
      <UserPageShell maxWidth="md" enter>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress sx={{ color: PRO_PINK_DEEP }} />
          <Typography variant="body1" sx={{ color: alpha(PRO_PINK_DEEP, 0.75) }}>
            {t.profile.loading}
          </Typography>
        </Box>
      </UserPageShell>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <UserPageShell maxWidth="md" enter>
      <Typography
        component="h1"
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: PRO_PINK_DEEP,
          fontSize: { xs: '1.2rem', sm: '1.5rem' },
          mb: { xs: 1.75, md: 2.5 },
        }}
      >
        {t.profile.title}
      </Typography>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              textAlign: 'center',
              height: '100%',
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(PRO_WARM, 0.28),
              boxShadow: `0 4px 14px ${alpha(PRO_WARM, 0.08)}`,
              background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PRO_WARM, 0.08)} 100%)`,
              ...userCardEnterSx(0),
            }}
          >
            <Avatar
              src={user?.avatarUrl || undefined}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                bgcolor: PRO_PINK_DEEP,
                border: '3px solid',
                borderColor: alpha(PRO_WARM, 0.55),
                boxShadow: `0 4px 16px ${alpha(PRO_PINK_DEEP, 0.25)}`,
              }}
            >
              {!user?.avatarUrl && <Person sx={{ fontSize: 60 }} />}
            </Avatar>

            <Button
              variant="outlined"
              component="label"
              size="small"
              disabled={uploadingAvatar}
              sx={{ ...outlinedPinkBtn, mb: 2, borderRadius: 999, px: 2 }}
            >
              {uploadingAvatar ? '...' : t.profile.changeAvatar}
              <input type="file" accept="image/*" hidden onChange={handleAvatarFileChange} />
            </Button>

            <Typography variant="h6" sx={{ color: PRO_PINK_DEEP, fontWeight: 800 }}>
              {user.username}
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(PRO_PINK_DEEP, 0.7), fontWeight: 600 }}>
              {user.role}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(PRO_WARM, 0.28),
              boxShadow: `0 4px 14px ${alpha(PRO_WARM, 0.08)}`,
              background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha(PRO_WARM, 0.06)} 100%)`,
              ...userCardEnterSx(1),
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Typography
                variant="h6"
                sx={{ color: PRO_PINK_DEEP, fontWeight: 800, mb: 0.25 }}
              >
                {t.profile.basicInfo}
              </Typography>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.7,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: alpha(PRO_PINK_DEEP, 0.78),
                  }}
                >
                  {t.profile.username}
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  autoComplete="username"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.7,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: alpha(PRO_PINK_DEEP, 0.78),
                  }}
                >
                  {t.profile.email}
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                  sx={fieldSx}
                />
              </Box>

              <Divider sx={{ my: 0.25, borderColor: alpha(PRO_WARM, 0.35) }}>
                <Typography variant="body2" sx={{ color: alpha(PRO_PINK_DEEP, 0.7), fontWeight: 600 }}>
                  {t.profile.passwordSection}
                </Typography>
              </Divider>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.7,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: alpha(PRO_PINK_DEEP, 0.78),
                  }}
                >
                  {t.profile.currentPassword}
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.7,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: alpha(PRO_PINK_DEEP, 0.78),
                  }}
                >
                  {t.profile.newPassword}
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  sx={fieldSx}
                />
              </Box>

              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    mb: 0.7,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: alpha(PRO_PINK_DEEP, 0.78),
                  }}
                >
                  {t.profile.confirmPassword}
                </Typography>
                <TextField
                  fullWidth
                  hiddenLabel
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  sx={fieldSx}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  mt: 0.5,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 800,
                  py: 1.2,
                  bgcolor: PRO_PINK_DEEP,
                  boxShadow: `0 8px 18px ${alpha(PRO_PINK_DEEP, 0.32)}`,
                  '&:hover': { bgcolor: '#d45a7a' },
                }}
              >
                {isLoading ? t.profile.submitting : t.profile.submit}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </UserPageShell>
  );
};

export default Profile;
